import { createContext, useContext, useState, useCallback, useEffect, PropsWithChildren } from 'react';
import { useUserAccounts } from '@trader-dashboard/shared';
import { TransactionalOrdersApi, TransactionalOrderUI } from '@/api/TransactionalOrdersApi';
import { useFunds } from './FundsContext';

interface TransactionalOrdersContextValue {
  orders: TransactionalOrderUI[];
  loading: boolean;
  error: string | undefined;
  linkToTransaction: (transactionalOrderId: number, fundsTransactionId: number) => Promise<void>;
  unlinkTransaction: (transactionalOrderId: number) => Promise<void>;
  createTransactionFromOrder: (transactionalOrderId: number, type: 'deposit' | 'withdrawal', amount: number, date: Date) => Promise<void>;
  refresh: () => Promise<void>;
}

const TransactionalOrdersContext = createContext<TransactionalOrdersContextValue | undefined>(undefined);

export const useTransactionalOrders = () => {
  const context = useContext(TransactionalOrdersContext);
  if (!context) {
    throw new Error('useTransactionalOrders must be used within TransactionalOrdersProvider');
  }
  return context;
};

export const TransactionalOrdersProvider = ({ children }: PropsWithChildren): React.ReactNode => {
  const [orders, setOrders] = useState<TransactionalOrderUI[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const { selectedAccountId } = useUserAccounts();
  const { add: addFundsTransaction, refresh: refreshFunds } = useFunds();

  const loadOrders = useCallback(async () => {
    if (!selectedAccountId) return;

    setLoading(true);
    setError(undefined);

    try {
      const data = await TransactionalOrdersApi.getByAccount(selectedAccountId);
      setOrders(data);
    } catch (err: any) {
      console.error('Failed to load transactional orders:', err);
      setError(err.message || 'Failed to load broker transactions');
    } finally {
      setLoading(false);
    }
  }, [selectedAccountId]);

  useEffect(() => {
    if (selectedAccountId) {
      loadOrders();
    }
  }, [selectedAccountId, loadOrders]);

  const linkToTransaction = useCallback(async (transactionalOrderId: number, fundsTransactionId: number) => {
    await TransactionalOrdersApi.linkToTransaction(transactionalOrderId, fundsTransactionId);
    await loadOrders();
  }, [loadOrders]);

  const unlinkTransaction = useCallback(async (transactionalOrderId: number) => {
    await TransactionalOrdersApi.unlinkTransaction(transactionalOrderId);
    await loadOrders();
  }, [loadOrders]);

  const createTransactionFromOrder = useCallback(async (
    transactionalOrderId: number,
    type: 'deposit' | 'withdrawal',
    amount: number,
    date: Date
  ) => {
    if (!selectedAccountId) return;

    // Create the funds transaction
    await addFundsTransaction(selectedAccountId, type, amount, date);

    // The addFundsTransaction refreshes funds internally, but we need the new transaction's ID
    // to link it. Refresh funds to get the latest, then find the matching transaction.
    // For now, we'll refresh funds and reload transactional orders.
    await refreshFunds(selectedAccountId);
    await loadOrders();
  }, [selectedAccountId, addFundsTransaction, refreshFunds, loadOrders]);

  return (
    <TransactionalOrdersContext.Provider value={{
      orders,
      loading,
      error,
      linkToTransaction,
      unlinkTransaction,
      createTransactionFromOrder,
      refresh: loadOrders,
    }}>
      {children}
    </TransactionalOrdersContext.Provider>
  );
};
